import React, { useCallback, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import beUpgradePlan from 'actions/billing/beUpgradePlan.action';
import SalonModal from 'components/dialog/SalonModal.component';
import Button from 'components/form/Button.component';
import InlineSpinner from 'components/generic/InlineSpinner.component';
import Spinner from 'components/generic/Spinner.component';
import DELAY from 'constants/delay.const';
import PLAN_INDEX from 'constants/planIndex.const';
import S from 'constants/string.const';
import { FeStack } from 'errors/fe.error';
import useAef from 'hooks/useAef.hook';
import useTip from 'hooks/useTip.hook';
import { useTranslation } from 'providers/GILT.provider';
import { selectCurrentSalonBetaAnnualSalon, selectCurrentSalonBilling, selectCurrentSalonId, selectCurrentSalonPricingPlan } from 'selectors/currentSalon.selectors';
import { selectBillingInvoices } from 'selectors/settings.selectors';
import { locationReload } from 'utils/dom/location.util';
import delayed from 'utils/functions/delayed.util';
import logger from 'utils/logger.util';
import { hasOverdueInvoices } from 'utils/salons/hasOverdueInvoices.util';
import salonSubscription from 'utils/salons/salonSubscription.util';

import { getBillingPlans, GetCustomerInvoices } from 'settings/billing/actions';
import { InactiveSubscriptionModal } from 'settings/billing/components/InactiveSubscriptionModal.component';
import { OverdueInvoiceModal } from 'settings/billing/components/OverdueInvoiceModal.component';
import BetaAnnualModal from 'settings/online-booking/modals/BetaAnnualModal';
import NoBillingModal from 'settings/online-booking/modals/NoBillingModal';

import CN from './UpgradePlanButton.module.scss';

const CTAG = 'UpgradePlanButton';
const L = logger(CTAG);

const SPINNER_COLOR = '#821bb8';
const SPINNER_BACKGROUND_COLOR = 'white';

const UpgradePlanButton = ({ text, children, variantWhite }) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();

  const salonId = useSelector(selectCurrentSalonId);
  const hasBilling = useSelector(selectCurrentSalonBilling);
  const pricingPlan = useSelector(selectCurrentSalonPricingPlan);
  const hasBetaAnnualSalon = useSelector(selectCurrentSalonBetaAnnualSalon);
  const invoices = useSelector(selectBillingInvoices);

  const { val: isUpgradeRendered, yay: showUpgrade, nay: hideUpgrade } = useTip(false);
  const { val: isBillingRendered, yay: showBilling, nay: hideBilling } = useTip(false);
  const { val: isBetaAnnualRendered, yay: showBetaAnnual, nay: hideBetaAnnual } = useTip(false);
  const { val: isLoading, yay: startLoading, nay: stopLoading } = useTip(false);
  const { val: isOverdueModal, yay: showOverdueModal, nay: hideOverdueModal } = useTip(false);

  const { val: isInactiveSubscriptionModal, yay: showInactiveSubscriptionModal, nay: hideInactiveSubscriptionModal } = useTip(false);

  useEffect(() => {
    if (!salonId) return;
    dispatch(GetCustomerInvoices(salonId, {}));
  }, [dispatch, salonId]);

  useAef(async () => {
    startLoading();
    try {
      await dispatch(getBillingPlans(salonId));
    } finally {
      delayed(DELAY.flash, stopLoading);
    }
  }, [dispatch, salonId, startLoading, stopLoading]);

  const invoicesLoaded = invoices && Array.isArray(invoices.results);

  // CLICK UPGRADE
  const handleUpgrade = useCallback(() => {
    if (!invoicesLoaded) return;
    if (hasOverdueInvoices(invoices)) {
      showOverdueModal();
      return;
    }
    if (hasBilling && hasBetaAnnualSalon) showBetaAnnual();
    if (!hasBilling && !hasBetaAnnualSalon) showBilling();

    showUpgrade();
  }, [invoicesLoaded, invoices, hasBilling, hasBetaAnnualSalon, showUpgrade, showBilling, showBetaAnnual, showOverdueModal]);

  // CONFIRM UPGRADE
  const handleConfirmUpgrade = useCallback(async () => {
    if (!salonId) return;

    startLoading();
    hideUpgrade();

    try {
      await dispatch(
        beUpgradePlan({
          planId: PLAN_INDEX.advanced,
          salonId,
        }),
      );
    } catch (error) {
      if (error?.code === 400) {
        showInactiveSubscriptionModal();
        return;
      }

      L.error('Upgrade failed with unexpected error', error);
    } finally {
      delayed(DELAY.flash, () => {
        locationReload(new FeStack({ tag: 'UpgradePlanButton__confirm-upgrade' }));
        stopLoading();
      });
    }
  }, [dispatch, salonId, hideUpgrade, startLoading, stopLoading, showInactiveSubscriptionModal]);

  const { error } = useAef(async () => {
    startLoading();
    try {
      await getBillingPlans(salonId);
    } finally {
      delayed(DELAY.flash, stopLoading);
    }
  }, [salonId, startLoading, stopLoading]);

  if (error) {
    L.warn('()', error);
  }

  const isComponentRendered = salonSubscription(hasBilling, pricingPlan);
  const displayPlanName = pricingPlan || t('common.custom');

  if (!isComponentRendered) return null;

  return (
    <div data-bem="UpgradePlanButton">
      <div className={CN.component}>
        <Button variant={variantWhite ? 'outline' : 'primaryForce'} onClick={handleUpgrade} disabled={!invoicesLoaded}>
          <span className={CN.buttonContent}>
            {!invoicesLoaded && <InlineSpinner variant="light" />} {text}
            {children}
          </span>
        </Button>
      </div>

      {isLoading && <Spinner color={SPINNER_COLOR} message="" background={SPINNER_BACKGROUND_COLOR} />}

      {isBillingRendered && <NoBillingModal isOpen onClose={hideBilling} />}

      {isBetaAnnualRendered && <BetaAnnualModal planName={displayPlanName} isOpen onClose={hideBetaAnnual} />}

      <OverdueInvoiceModal isOpen={isOverdueModal} onClose={hideOverdueModal} />

      <InactiveSubscriptionModal isOpen={isInactiveSubscriptionModal} onClose={hideInactiveSubscriptionModal} />

      {isUpgradeRendered && (
        <SalonModal tag="UpgradePlanButton" isOpen>
          <SalonModal.Body>
            <div className={CN.modalContent}>
              <div className={CN.modalTitle}>{t('composite.upgradePlanModal.title', { suffix: S.fist })}</div>

              <div className={CN.modalText}>{t('composite.upgradePlanModal.descriptionPart1')}</div>
              <div className={CN.modalText}>{t('composite.upgradePlanModal.descriptionPart2')}</div>
            </div>
          </SalonModal.Body>

          <SalonModal.Footer>
            <Button variant="slim" onClick={hideUpgrade}>
              {t('composite.upgradePlanModal.cancel')}
            </Button>

            <Button variant="primaryForce" casing="dialog" xSpace onClick={handleConfirmUpgrade}>
              {t('composite.upgradePlanModal.confirm')}
            </Button>
          </SalonModal.Footer>
        </SalonModal>
      )}
    </div>
  );
};

export default UpgradePlanButton;
